// Today on a phone, after SPhoneHome: the top bar (search, notifications, you), the date over a large Today, Your
// day (the assistant's two sentences on what matters today), Needs you (the first 3, then Show all), Reminders
// (Today and Tomorrow, then "Remind me to..."), This week, and Gone quiet. Every row is one line with a status line
// and opens a sheet: a move's sheet has its actions; a reminder or a day's item opens the Today drawer.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import type { ProposedAction, Reminder, SessionUser, TeamCheck, TodayData, TodayItem } from '@dpw/shared';
import { localTime, gbDate } from '../lib/format';
import { spring } from '../lib/motion';
import { AddReminderForm } from '../ui/AddReminderForm';
import { DrawnCheck, Skel, SkelList } from '../ui/motion';
import { Sheet } from '../ui/Sheet';
import { TodayDrawer, type DrawerState } from '../pages/today/Drawer';
import { useMoves, type Act, type Move, type QuietRow } from '../pages/today/moves';
import { TYPE_COLOR, TYPE_WORD, dayNum, isMeeting, progressColor, remindersFor, weekdayOf } from '../pages/today/util';
import { AvatarButton, BellButton, BriefCard, P, PSheet, Ring, SearchButton, Section, Tag, Title, TopBar } from './kit';

/** Needs you shows this many rows on a phone, then "Show all N". */
export const PHONE_ROWS = 3;
/** This week shows this many rows, then "Show all N". */
const WEEK_ROWS = 7;
const KK: Record<string, string> = { Approval: 'appr', Confirm: 'q', Question: 'q', Prep: 'prep', 'At risk': 'risk' };
const TAG: Record<string, 'g' | 'a' | 'r' | ''> = { Approval: 'g', Confirm: '', Question: '', Prep: 'a', 'At risk': 'r' };
const eyebrow = (d: string) => gbDate(new Date(`${d}T12:00:00Z`), { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

function remMeta(r: Reminder) {
  const by = r.setBy === 'assistant' ? 'Set by the assistant' : r.setByOther ? `Set by ${r.setByUser?.name.split(' ')[0] ?? 'someone'} for you` : 'Your reminder';
  return r.activity ? `${by} · ${r.activity.title}` : by;
}

export function PhoneToday({ me, t, reminders, actions, checks, onToggle }: {
  me: SessionUser; t: TodayData; reminders: Reminder[]; actions: ProposedAction[]; checks: TeamCheck[]; onToggle: (r: Reminder) => void;
}) {
  const mv = useMoves({ me, actions, checks, yours: t.yours, quiet: t.quiet });
  const [all, setAll] = useState(false);
  const [allWeek, setAllWeek] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  const [quietSel, setQuietSel] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [adding, setAdding] = useState(false);

  const today = remindersFor(reminders, 'today', t.date);
  const overdue = today.filter(r => r.state === 'overdue');
  const groups: [string, Reminder[]][] = [['Today', today.filter(r => r.state !== 'overdue')], ['Tomorrow', remindersFor(reminders, 'tomorrow', t.date)]];
  const moves = [...mv.aiShown, ...mv.mineShown];
  const ordered = [...moves.filter(m => !mv.done[m.key]), ...moves.filter(m => mv.done[m.key])];
  // Overdue reminders lead Needs you (tick them here); then the moves. The first 3, then Show all.
  const rows: ({ r: Reminder } | { m: Move })[] = [...overdue.map(r => ({ r })), ...ordered.map(m => ({ m }))];
  const left = overdue.length + mv.open;
  const doneN = mv.cleared;
  const cut = !all && rows.length > PHONE_ROWS;
  const shown = cut ? rows.slice(0, PHONE_ROWS) : rows;
  const cur = moves.find(m => m.key === sel);
  const quiet = mv.quietRows.find(r => r.key === quietSel);
  const week = t.week.flatMap(d => d.items.map(x => ({ ...x, date: d.date })));
  const weekShown = allWeek ? week : week.slice(0, WEEK_ROWS);
  const items = [...t.meetings, ...t.due.today, ...t.due.tomorrow, ...week];

  const act = (a: Act) => a.to
    ? <Link key={a.label} className={`ph-btf${a.style === 'pri' ? '' : ' sec'}`} to={a.to}>{a.label}</Link>
    : <button key={a.label} type="button" className={`ph-btf${a.style === 'pri' ? '' : ' sec'}`} disabled={a.disabled} onClick={() => { a.run?.(); setSel(null); }}>{a.label}</button>;

  // Voice control from the top of Today (Kareem, 4 Nov), as well as the row in More.
  return (
    <div className="ph-page ph-today">
      <TopBar lead={<AvatarButton initials={me.initials} />}><SearchButton /><BellButton /></TopBar>
      <Title eyebrow={eyebrow(t.date)} title="Today" sub={left ? `${left} ${left === 1 ? 'thing needs' : 'things need'} you, ${doneN} done` : doneN ? 'All clear. Nice work.' : 'Nothing needs you.'} />
      <BriefCard kind="day" id={me.id} label="Your day" />

      <Section title="Needs you" count={left} />
      <div className="ph-grp" role="group" aria-label="Needs you">
        {shown.map(x => 'r' in x ? (
          <div key={x.r.id} className="ph-rowx" data-row>
            <button type="button" className={`ph-ck${x.r.state === 'done' ? ' on' : ''}`} aria-label={`Done: ${x.r.text}`} aria-pressed={x.r.state === 'done'} onClick={() => onToggle(x.r)}>{x.r.state === 'done' ? <DrawnCheck size={14} /> : <P.check />}</button>
            <button type="button" className="ph-rbtn" onClick={() => setDrawer({ kind: 'reminder', id: x.r.id })}>
              <span className="ph-rt"><div className="t">{x.r.text}</div><div className="s"><b className="kk risk">Overdue</b> · Your reminder</div></span><P.chev />
            </button>
          </div>
        ) : mv.done[x.m.key] ? (
          <motion.div key={x.m.key} layout="position" transition={spring.gentle} className="ph-rowx done" data-row data-card={x.m.key}>
            <span className="ph-ic grn"><P.check /></span>
            <span className="ph-rt"><div className="t">{x.m.title}</div><div className="res" role="status">{mv.done[x.m.key]!.result}</div></span>
            {mv.done[x.m.key]!.undo && <button type="button" className="ph-btm" onClick={mv.done[x.m.key]!.undo}>Undo</button>}
          </motion.div>
        ) : (
          <motion.button key={x.m.key} layout="position" transition={spring.gentle} type="button" className={`ph-row${x.m.ai ? ' ait' : ''}`} data-row data-card={x.m.key} data-kind={x.m.kind} onClick={() => setSel(x.m.key)}>
            <span className={`ph-ic ${x.m.tone}`}>{x.m.icon}</span>
            <span className="ph-rt"><div className="t">{x.m.title}</div><div className="s"><b className={`kk ${KK[x.m.lbl[0]] ?? ''}`}>{x.m.lbl[0]}</b>{x.m.ctx ? ` · ${x.m.ctx.label}` : ''}</div></span>
            <P.chev />
          </motion.button>
        ))}
        {!rows.length && <div className="ph-rowx"><span className="ph-rt"><div className="t">You are clear</div><div className="s">Nothing is blocked on you.</div></span></div>}
        {rows.length > PHONE_ROWS && (
          <button type="button" className="ph-row more2" onClick={() => setAll(!all)}><span className="ph-rt"><div className="t">{all ? 'Show fewer' : `Show all ${rows.length}`}</div></span><P.chev /></button>
        )}
      </div>

      <Section title="Reminders" right={<Link to="/reminders">All</Link>} />
      {groups.map(([h, list], gi) => (
        <div key={h}>
          <div className="ph-rgh">{h}</div>
          <div className="ph-grp" role="group" aria-label={`Reminders ${h.toLowerCase()}`}>
            {list.map(r => (
              <div key={r.id} className={`ph-rowx${r.setBy === 'assistant' ? ' ait' : ''}`}>
                <button type="button" className={`ph-ck${r.state === 'done' ? ' on' : ''}`} aria-label={`Done: ${r.text}`} aria-pressed={r.state === 'done'} onClick={() => onToggle(r)}>{r.state === 'done' ? <DrawnCheck size={14} /> : <P.check />}</button>
                <button type="button" className="ph-rbtn" onClick={() => setDrawer({ kind: 'reminder', id: r.id })}>
                  <span className="ph-rt"><div className="t" style={r.state === 'done' ? { textDecoration: 'line-through', color: 'var(--faint)' } : undefined}>{r.text}</div>
                    <div className="s">{r.setBy === 'assistant' && <span className="aiorb" />}{remMeta(r)}</div></span>
                  <span className="ph-tr mo f13">{r.remindAt ? localTime(r.remindAt) : 'Any time'}</span>
                </button>
              </div>
            ))}
            {!list.length && <div className="ph-rowx"><span className="ph-rt"><div className="s">Nothing for {h.toLowerCase()}.</div></span></div>}
            {gi === groups.length - 1 && (
              <button type="button" className="ph-row rmadd" onClick={() => setAdding(true)}><span className="ph-ck plus"><P.plus /></span><span className="ph-rt"><div className="t">Remind me to...</div></span></button>
            )}
          </div>
        </div>
      ))}

      <Section title="This week" right={<Link to="/calendar">Calendar</Link>} />
      <div className="ph-grp" role="group" aria-label="This week">
        {weekShown.map(x => <WeekRow key={`${x.date}-${x.id}`} x={x} today={t.date} onOpen={() => setDrawer({ kind: 'item', id: x.id })} />)}
        {!week.length && <div className="ph-rowx"><span className="ph-rt"><div className="s">Nothing dated this week.</div></span></div>}
        {week.length > WEEK_ROWS && (
          <button type="button" className="ph-row more2" onClick={() => setAllWeek(!allWeek)}><span className="ph-rt"><div className="t">{allWeek ? 'Show fewer' : `Show all ${week.length}`}</div></span><P.chev /></button>
        )}
      </div>

      <Section title="Gone quiet" count={mv.quietRows.filter(r => !r.done).length || undefined} />
      <div className="ph-grp" role="group" aria-label="Gone quiet">
        {mv.quietRows.map(r => <QuietLine key={r.key} r={r} onOpen={() => setQuietSel(r.key)} />)}
        {!mv.quietRows.length && <div className="ph-rowx"><span className="ph-rt"><div className="s">Nothing has gone quiet.</div></span></div>}
      </div>

      <AnimatePresence>
        {cur && !mv.done[cur.key] && (
          <PSheet key="move" label={cur.title} onClose={() => setSel(null)} card={{ id: cur.key, kind: cur.kind }}>
            <div className="ph-hr"><Tag tone={TAG[cur.lbl[0]] ?? ''}>{cur.lbl[0]}</Tag>{cur.ctx && <Link className="f13 mu ph-tl" to={cur.ctx.to}>{cur.ctx.label}</Link>}</div>
            <h2>{cur.title}</h2>
            <p className="why">{cur.why}</p>
            <div className="gap8" />
            {cur.answer ? (
              <form className="ph-ansb" onSubmit={e => { e.preventDefault(); mv.sendAnswer(cur); setSel(null); }}>
                <input className="ph-inp" data-autofocus value={mv.answerOf(cur)} onChange={e => mv.setAnswer(cur, e.target.value)} placeholder="Your answer" aria-label={`Your answer: ${cur.answer.question}`} />
                <button className="ph-btf" disabled={!mv.answerOf(cur).trim()}>Answer</button>
              </form>
            ) : cur.acts.map(act)}
            <button type="button" className="ph-btf ghost" onClick={() => setSel(null)}>Not now</button>
          </PSheet>
        )}
        {quiet && !quiet.done && (
          <PSheet key="quiet" label={quiet.q.title} onClose={() => setQuietSel(null)}>
            <div className="ph-hr"><Tag tone={quiet.q.tone === 'red' ? 'r' : 'a'}>Gone quiet</Tag><span className="f13 mu">{quiet.q.days} days</span></div>
            <h2>{quiet.q.title}</h2>
            <p className="why">{quiet.q.why}</p>
            <div className="gap8" />
            {quiet.nudge && <button type="button" className="ph-btf" onClick={() => { quiet.nudge!(); setQuietSel(null); }}>Nudge {quiet.who}</button>}
            <button type="button" className={`ph-btf${quiet.nudge ? ' sec' : ''}`} onClick={() => { quiet.fine(); setQuietSel(null); }}>It is fine</button>
            <Link className="ph-btf sec" to={quiet.to}>Open</Link>
            <button type="button" className="ph-btf ghost" onClick={() => setQuietSel(null)}>Not now</button>
          </PSheet>
        )}
        {drawer && <TodayDrawer key="drawer" state={drawer} items={items} reminders={reminders} today={t.date} phone onClose={() => setDrawer(null)} onOpen={setDrawer}
          onDone={r => { onToggle(r); setDrawer(null); }} />}
        {adding && (
          <Sheet key="add" side="bottom" label="Add a reminder" onClose={() => setAdding(false)} crumbs={<><span>Today</span><span>/</span><b>Add a reminder</b></>}>
            <AddReminderForm me={me} autoFocus onSaved={() => setAdding(false)} />
          </Sheet>
        )}
      </AnimatePresence>
    </div>
  );
}

function WeekRow({ x, today, onOpen }: { x: TodayItem; today: string; onOpen: () => void }) {
  const meet = isMeeting(x.type);
  const due = !meet && !x.start ? 'Due' : !meet ? `Due ${x.start}` : null;
  return (
    <button type="button" className="ph-row" onClick={onOpen} data-row>
      <span className={`ph-dbx${x.date === today ? ' today' : ''}`}><span>{weekdayOf(x.date)}</span><b>{dayNum(x.date)}</b></span>
      <span className="ph-rt">
        <div className="t">{x.isMilestone && <span className="ph-dia" aria-label="Milestone" />}{x.title}</div>
        <div className="s"><span className="ph-tdot" style={{ background: TYPE_COLOR[x.type] }} />{due && <><b className="duef">{due}</b> · </>}{meet && x.start ? `${x.start} · ` : ''}{TYPE_WORD[x.type]} · {x.trackName}</div>
      </span>
      {x.stepsTotal > 0 || !meet
        ? (x.progress >= 100 ? <span className="ph-rg" style={{ background: 'var(--grn)' }} aria-label="Done"><span style={{ color: 'var(--grn-tx)' }}><P.check /></span></span>
          : <Ring pct={x.progress} color={progressColor(x)}>{x.progress}%</Ring>)
        : <span className="f12 fa">No prep</span>}
    </button>
  );
}

function QuietLine({ r, onOpen }: { r: QuietRow; onOpen: () => void }) {
  return (
    <div className={`ph-rowx${r.done ? ' done' : ''}`} data-row>
      <button type="button" className="ph-rbtn" onClick={onOpen} disabled={!!r.done}>
        <span className={`ph-ic ${r.done ? 'grn' : ''}`}>{r.done ? <P.check /> : <P.clock />}</span>
        <span className="ph-rt"><div className="t">{r.q.title}</div>{r.done ? <div className="res" role="status">{r.done.result}</div> : <div className="s">{r.q.why}</div>}</span>
      </button>
      {r.done ? r.done.undo && <button type="button" className="ph-btm" onClick={r.done.undo}>Undo</button>
        : r.nudge ? <button type="button" className="ph-btm" onClick={r.nudge}>Nudge {r.who}</button>
        : <button type="button" className="ph-btm" onClick={r.fine}>It is fine</button>}
    </div>
  );
}

/** While Today loads on a phone: the title block, Your day and the first rows, in their places. */
export function PhoneTodaySkeleton() {
  return (
    <div className="ph-page" role="status" aria-label="Loading">
      <div className="ph-navb" />
      <div className="ph-lt"><Skel w="40%" h={12} /><div className="gap8" /><Skel w="45%" h={34} r={8} /><div className="gap8" /><Skel w="50%" h={13} /></div>
      <Skel h={92} r={18} />
      <div className="ph-gh"><Skel w="34%" h={18} /></div>
      <div className="ph-grp" style={{ padding: 14 }}><SkelList rows={3} variant="conv" /></div>
    </div>
  );
}
